import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  BugReport as DiagnosticsIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  WarningAmber as WarningIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import { devicesApi, type Device } from '../../services/devicesApi';
import { drContinuityApi, type SqliteQuickCheckResult } from '../../services/drContinuityApi';
import { useAuth } from '../../contexts/AuthContext';

function jsonPreview(v: any, max = 900): string {
  try {
    const s = JSON.stringify(v ?? null, null, 2);
    if (s.length <= max) return s;
    return s.slice(0, max) + '\n…';
  } catch {
    return String(v ?? '');
  }
}

function outboxChipColor(summary: any): 'default' | 'success' | 'warning' | 'error' {
  const all = summary?.outbox_all || {};
  const pending = Number(all?.count_pending ?? 0);
  const leased = Number(all?.count_leased ?? 0);
  const failed = Number(all?.count_failed ?? 0);
  const quarantined = Number(all?.count_quarantined ?? 0);
  if (failed > 0 || quarantined > 0) return 'error';
  if (pending > 0 || leased > 0) return 'warning';
  return 'success';
}

function sqliteChip(result: SqliteQuickCheckResult | null): { label: string; color: 'default' | 'success' | 'warning' | 'error' } {
  if (!result) return { label: 'unknown', color: 'default' };
  if (result.skipped) return { label: `skipped (${result.skipped})`, color: 'default' };
  if (result.exists === false) return { label: 'db missing', color: 'warning' };
  if (result.ok === true) return { label: 'ok', color: 'success' };
  if (result.ok === false) return { label: 'corrupt', color: 'error' };
  if (result.error) return { label: String(result.error), color: 'error' };
  return { label: 'unknown', color: 'default' };
}

const DisasterRecoveryContinuity: React.FC = () => {
  const { effectiveUser: user } = useAuth();
  const capabilities = Array.isArray((user as any)?.capabilities) ? (user as any).capabilities : [];
  const canWriteDevices = capabilities.includes('devices:write');
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDeviceId = String(searchParams.get('device') || '').trim();

  const [devices, setDevices] = useState<Device[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState<string | null>(null);

  const selectedDevice: Device | null = useMemo(() => {
    if (!selectedDeviceId) return null;
    return devices.find((d) => d.device_id === selectedDeviceId) || null;
  }, [devices, selectedDeviceId]);

  const [outboxSummary, setOutboxSummary] = useState<any>(null);
  const [outboxLoading, setOutboxLoading] = useState(false);
  const [outboxError, setOutboxError] = useState<string | null>(null);

  const [sqliteResult, setSqliteResult] = useState<SqliteQuickCheckResult | null>(null);
  const [sqliteLoading, setSqliteLoading] = useState(false);
  const [sqliteError, setSqliteError] = useState<string | null>(null);

  const [drillIncidentId, setDrillIncidentId] = useState<string>('');
  const [drillWaitSec, setDrillWaitSec] = useState<number>(10);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillResult, setDrillResult] = useState<any>(null);
  const [drillError, setDrillError] = useState<string | null>(null);

  const loadDevices = useCallback(async () => {
    setDevicesLoading(true);
    setDevicesError(null);
    try {
      const ds = await devicesApi.listDevices();
      setDevices(ds);
      // Auto-pick the only device (or first) when none is selected.
      if (!selectedDeviceId && ds.length === 1) {
        setSearchParams({ device: ds[0].device_id });
      }
    } catch (e: any) {
      setDevices([]);
      setDevicesError(e?.response?.data?.error || e?.message || 'Failed to load devices');
    } finally {
      setDevicesLoading(false);
    }
  }, [selectedDeviceId, setSearchParams]);

  useEffect(() => {
    void loadDevices();
  }, [loadDevices]);

  const refreshOutbox = useCallback(async () => {
    if (!selectedDeviceId) return;
    setOutboxLoading(true);
    setOutboxError(null);
    try {
      const data = await drContinuityApi.getOutboxSummary(selectedDeviceId);
      setOutboxSummary(data);
    } catch (e: any) {
      setOutboxSummary(null);
      setOutboxError(e?.response?.data?.error || e?.message || 'outbox_summary_failed');
    } finally {
      setOutboxLoading(false);
    }
  }, [selectedDeviceId]);

  const runSqliteQuickCheck = useCallback(async () => {
    if (!selectedDeviceId) return;
    setSqliteLoading(true);
    setSqliteError(null);
    try {
      const data = await drContinuityApi.sqliteQuickCheck(selectedDeviceId);
      setSqliteResult(data);
    } catch (e: any) {
      setSqliteResult(null);
      setSqliteError(e?.response?.data?.error || e?.message || 'sqlite_quick_check_failed');
    } finally {
      setSqliteLoading(false);
    }
  }, [selectedDeviceId]);

  const runDrillD = useCallback(async () => {
    if (!selectedDeviceId) return;
    setDrillLoading(true);
    setDrillError(null);
    setDrillResult(null);
    try {
      const payload: any = {};
      if (drillIncidentId.trim()) payload.incident_id = drillIncidentId.trim();
      if (Number.isFinite(drillWaitSec) && drillWaitSec > 0) payload.wait_sec = Math.floor(drillWaitSec);
      const data = await drContinuityApi.runCorruptEvidenceQuarantineDrill(selectedDeviceId, payload);
      setDrillResult(data);
    } catch (e: any) {
      setDrillResult(null);
      setDrillError(e?.response?.data?.error || e?.message || 'drill_failed');
    } finally {
      setDrillLoading(false);
    }
  }, [selectedDeviceId, drillIncidentId, drillWaitSec]);

  // Refresh signals when switching devices.
  useEffect(() => {
    setOutboxSummary(null);
    setOutboxError(null);
    setSqliteResult(null);
    setSqliteError(null);
    setDrillResult(null);
    setDrillError(null);
    if (selectedDeviceId) {
      void refreshOutbox();
      void runSqliteQuickCheck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeviceId]);

  const sqliteChipState = sqliteChip(sqliteResult);

  return (
    <Box sx={{ p: 3 }}>
      {!canWriteDevices && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have read-only device access. Recovery drills are disabled.
        </Alert>
      )}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <StorageIcon />
        <Typography variant="h4">DR & Continuity</Typography>
        <Chip size="small" label="Epic 5" />
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="dr-device-label">Device</InputLabel>
              <Select
                labelId="dr-device-label"
                label="Device"
                value={selectedDeviceId}
                onChange={(e) => setSearchParams({ device: String(e.target.value || '') })}
              >
                {devices.map((d) => (
                  <MenuItem key={d.device_id} value={d.device_id}>
                    {d.device_id} {d.name ? `• ${d.name}` : ''} {d.ip_address ? `• ${d.ip_address}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={devicesLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={() => void loadDevices()}
                disabled={devicesLoading}
              >
                Refresh devices
              </Button>
              <Button
                variant="contained"
                startIcon={outboxLoading || sqliteLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={() => {
                  void refreshOutbox();
                  void runSqliteQuickCheck();
                }}
                disabled={!selectedDeviceId || outboxLoading || sqliteLoading}
              >
                Refresh signals
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {devicesError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {devicesError}
          </Alert>
        )}
        {!devicesLoading && devices.length === 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            No devices found for the current org/site scope.
          </Alert>
        )}
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardHeader
              avatar={<DiagnosticsIcon />}
              title="Outbox Summary"
              subheader={selectedDevice ? `${selectedDevice.device_id}${selectedDevice.ip_address ? ` • ${selectedDevice.ip_address}` : ''}` : '—'}
              action={<Chip size="small" label={outboxSummary ? 'loaded' : '—'} color={outboxSummary ? outboxChipColor(outboxSummary) : 'default'} />}
            />
            <CardContent>
              {outboxError && <Alert severity="error">{outboxError}</Alert>}
              {!outboxError && !outboxSummary && (
                <Typography variant="body2" color="text.secondary">
                  Select a device to load outbox summary.
                </Typography>
              )}
              {outboxSummary && (
                <>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={`pending=${outboxSummary?.outbox_all?.count_pending ?? 0}`} />
                    <Chip size="small" label={`leased=${outboxSummary?.outbox_all?.count_leased ?? 0}`} />
                    <Chip size="small" label={`failed=${outboxSummary?.outbox_all?.count_failed ?? 0}`} color={(Number(outboxSummary?.outbox_all?.count_failed ?? 0) > 0) ? 'error' : 'default'} />
                    <Chip size="small" label={`quarantined=${outboxSummary?.outbox_all?.count_quarantined ?? 0}`} color={(Number(outboxSummary?.outbox_all?.count_quarantined ?? 0) > 0) ? 'error' : 'default'} />
                    <Chip size="small" label={`oldest_pending_age_sec=${outboxSummary?.outbox_all?.oldest_pending_age_sec ?? '—'}`} />
                    {Array.isArray(outboxSummary?.warnings) && outboxSummary.warnings.length > 0 && (
                      <Chip size="small" icon={<WarningIcon />} label={`warnings=${outboxSummary.warnings.join(', ')}`} color="warning" />
                    )}
                  </Stack>
                  <Divider sx={{ my: 1 }} />
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa' }}>
                    <Typography variant="caption" color="text.secondary">
                      Raw
                    </Typography>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                      {jsonPreview(outboxSummary, 1600)}
                    </pre>
                  </Paper>
                </>
              )}
            </CardContent>
            <CardActions>
              <Button
                size="small"
                startIcon={outboxLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={() => void refreshOutbox()}
                disabled={!selectedDeviceId || outboxLoading}
              >
                Refresh
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ mb: 2 }}>
            <CardHeader
              avatar={<StorageIcon />}
              title="SQLite quick check (outbox DB)"
              subheader={sqliteResult?.db_path ? sqliteResult.db_path : 'Edge: /control/outbox/sqlite_quick_check'}
              action={<Chip size="small" label={sqliteChipState.label} color={sqliteChipState.color} />}
            />
            <CardContent>
              {sqliteError && <Alert severity="error">{sqliteError}</Alert>}
              {!sqliteError && sqliteResult && sqliteResult.result && (
                <Alert severity={sqliteResult.ok ? 'success' : 'error'}>
                  {sqliteResult.ok ? 'OK' : 'NOT OK'}: {String(sqliteResult.result).slice(0, 160)}
                </Alert>
              )}
              {!sqliteError && !sqliteResult && (
                <Typography variant="body2" color="text.secondary">
                  Select a device to run quick check.
                </Typography>
              )}
            </CardContent>
            <CardActions>
              <Button
                size="small"
                startIcon={sqliteLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={() => void runSqliteQuickCheck()}
                disabled={!selectedDeviceId || sqliteLoading}
              >
                Run quick check
              </Button>
            </CardActions>
          </Card>

          <Card>
            <CardHeader
              avatar={<PlayIcon />}
              title="Safe drill: corrupt evidence → QUARANTINED (Drill D)"
              subheader="Requires HEALTH_API_DRILLS_ENABLED=1 on the edge"
            />
            <CardContent>
              <Stack spacing={1.5}>
                <TextField
                  size="small"
                  label="incident_id (optional)"
                  value={drillIncidentId}
                  onChange={(e) => setDrillIncidentId(e.target.value)}
                  placeholder="gate2D-1 (optional override)"
                />
                <TextField
                  size="small"
                  type="number"
                  label="wait_sec"
                  value={drillWaitSec}
                  onChange={(e) => setDrillWaitSec(Number(e.target.value))}
                  inputProps={{ min: 1, max: 120 }}
                />
                {drillError && <Alert severity="error">{drillError}</Alert>}
                {drillResult && (
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa' }}>
                    <Typography variant="caption" color="text.secondary">
                      Result
                    </Typography>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                      {jsonPreview(drillResult, 1400)}
                    </pre>
                  </Paper>
                )}
              </Stack>
            </CardContent>
            <CardActions>
              <Button
                size="small"
                variant="contained"
                startIcon={drillLoading ? <CircularProgress size={16} /> : <PlayIcon />}
                onClick={() => void runDrillD()}
                disabled={!canWriteDevices || !selectedDeviceId || drillLoading}
              >
                Run Drill D
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DisasterRecoveryContinuity;
